import * as Sentry from '@sentry/react'
import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { queryClient } from '@/lib/query-client'
import {
  TenantContext,
  clearStoredTenantDisplayName,
  clearStoredTenantSlug,
  clearStoredTenantUserId,
  getStoredTenantDisplayName,
  getStoredTenantSlug,
  setStoredTenantDisplayName,
  setStoredTenantSlug,
  setStoredTenantUserId,
} from '@/lib/tenant'

interface TenantProviderProps {
  children: ReactNode
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenantSlug, setTenantSlugState] = useState<string | null>(getStoredTenantSlug)
  const [tenantDisplayName, setTenantDisplayNameState] = useState<string | null>(
    getStoredTenantDisplayName,
  )

  const setTenant = useCallback(
    (slug: string, displayName: string, userId: string) => {
      if (slug !== tenantSlug) {
        queryClient.clear()
      }
      setStoredTenantSlug(slug)
      setStoredTenantDisplayName(displayName)
      setStoredTenantUserId(userId)
      setTenantSlugState(slug)
      setTenantDisplayNameState(displayName)
      Sentry.setTag('tenant_slug', slug)
    },
    [tenantSlug],
  )

  const clearTenant = useCallback(() => {
    clearStoredTenantSlug()
    clearStoredTenantDisplayName()
    clearStoredTenantUserId()
    setTenantSlugState(null)
    setTenantDisplayNameState(null)
    Sentry.setTag('tenant_slug', undefined)
  }, [])

  const contextValue = useMemo(
    () => ({ tenantSlug, tenantDisplayName, setTenant, clearTenant }),
    [tenantSlug, tenantDisplayName, setTenant, clearTenant],
  )

  return <TenantContext value={contextValue}>{children}</TenantContext>
}
